import React, { useState, useLayoutEffect, useRef, useCallback } from "react";
import Map from "./Map";
import { Modal, Button } from "antd";
import AutoComplete from "./AutoComplete";
import Surface from "../layouts/Surface";
import LocationDetails from "./LocationDetails";
import LocationWrapper from "./style";
import OrientedDivider from "../util/OrientedDivider";
import { FormattedMessage } from "react-intl";
import debounce from "lodash.debounce";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import googleMap from "../../assets/googleMap.png";
import { useIntl } from "react-intl";
import { useJsApiLoader } from "@react-google-maps/api";
const libraries = ["places"];

const LocationPicker = (props) => {
  const { activeGeo, city, street, houseNumber, onLocationChanged } = props;
  const intl = useIntl();
  const [isMapShown, setIsMapShown] = useState(false); // State to control map visibility
  const [visible, setVisible] = useState(false);
  const [newLocation, setNewLocation] = useState(false);
  const [modalLocation, setModalLocation] = useState(activeGeo);
  const setModalVisible = (val) => {
    setVisible(val);
    if (val) {
      setNewLocation(false);
    }
  };
  const [err, setErr] = useState(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDs2QkqANgCTQIcThQFCiNkxcKGPVkS_lM",
    libraries: libraries,
    language: "he",
    loadingStrategy: "ASYNC",
    id: "google-map-script",
  });
  const debouncedGetNewAddress = useCallback(
    debounce((addr) => {
      getNewAddress(addr);
    }, 400),
    [isLoaded, visible, isMapShown]
  );

  const firstUpdate = useRef(true);
  useLayoutEffect(() => {
    if (!isMapShown) {
      return;
    }
    if (firstUpdate.current && activeGeo?.address_line) {
      firstUpdate.current = false;
      return;
    }

    if (city && street && houseNumber) {
      const address = city + ", " + street + " " + houseNumber;

      debouncedGetNewAddress(address);
    }
  }, [
    city,
    street,
    houseNumber,
    debouncedGetNewAddress,
    visible,
    isLoaded,
    isMapShown,
  ]);

  const getNewAddress = (address) => {
    if (window?.google) {
      const places = new window.google.maps.places.AutocompleteService();
      places.getPlacePredictions(
        {
          ...props.searchOptions,
          input: address,
        },
        autocompleteCallback
      );
    }
  };

  const autocompleteCallback = async (predictions, status) => {
    if (predictions && predictions[0]) {
      const results = await geocodeByAddress(predictions[0].description);
      const latLng = await getLatLng(results[0]);
      const geo = {
        longtitude: latLng.lng,
        latitude: latLng.lat,
        address_line: predictions[0].description,
      };
      if (
        latLng &&
        (activeGeo?.longtitude !== geo?.longtitude ||
          activeGeo?.latitude !== geo?.latitude)
      ) {
        onLocationChanged(geo);
      }
    }
  };

  const updateLocation = (lat, lng, addr) => {
    setNewLocation(true);
    const geo = {
      longtitude: lng,
      latitude: lat,
      address_line: addr,
    };
    setModalLocation(geo);
  };

  const onError = (error) => {
    setErr(error);
  };

  const onSave = () => {
    onLocationChanged(modalLocation);
    setModalVisible(false);
    setModalLocation(modalLocation);
  };

  const onCancel = () => {
    setModalVisible(false);
    setModalLocation(activeGeo);
  };

  const renderPlaceholder = () => (
    <div style={{ width: "100%" }}>
      <img
        src={googleMap}
        alt="Map Placeholder"
        style={{ width: "100%", height: "100%", filter: "blur(5px)" }}
      />
      <Button
        type="primary"
        onClick={() => setIsMapShown(true)}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        {intl.formatMessage({
          id: "app.forms.location.reveal_map",
        })}
      </Button>
    </div>
  );
  return (
    <div>
      <div>
        <Button
          style={{ marginBottom: "20px" }}
          type="primary"
          onClick={() => setModalVisible(true)}
        >
          <FormattedMessage id="app.actions.changeLocation" />
        </Button>
        {isMapShown ? (
          <Map
            marker={
              activeGeo
                ? {
                    latitude: activeGeo.latitude,
                    longitude: activeGeo.longtitude,
                  }
                : null
            }
            type={"mini"}
          />
        ) : (
          renderPlaceholder()
        )}
      </div>

      <Modal
        destroyOnClose={true}
        style={{ padding: 0, height: "100%" }}
        styles={{
          body: {
            padding: 0,
          },
        }}
        maskStyle={{ height: "100%" }}
        width="90%"
        height="90%"
        centered
        footer={null}
        title={null}
        closable={false}
        open={visible}
        onOk={() => setModalVisible(false)}
        onCancel={() => setModalVisible(false)}
      >
        <LocationWrapper
          style={{ width: "100%", height: "100%", display: "flex" }}
        >
          <Surface
            style={{
              width: "400px",
            }}
          >
            <OrientedDivider
              message={<FormattedMessage id="app.forms.location" />}
            />
            <AutoComplete updateLocation={updateLocation} onError={onError} />
            {newLocation && modalLocation ? (
              <LocationDetails
                latitude={modalLocation.latitude}
                longitude={modalLocation.longtitude}
                address={modalLocation.address_line}
                err={err}
              />
            ) : null}

            {modalLocation && modalLocation.longtitude ? (
              <div
                style={{
                  paddingTop: "20px",
                }}
              >
                <Button
                  type="primary"
                  block
                  onClick={onSave}
                  style={{
                    marginBottom: "10px",
                  }}
                >
                  <FormattedMessage id="app.actions.saveLocation" />
                </Button>
                <Button block onClick={onCancel}>
                  <FormattedMessage id="app.actions.cancel" />
                </Button>
              </div>
            ) : null}
          </Surface>
          <div
            style={{
              padding: "10px",
              width: "100%",
              height: "100%",
              borderRadius: "10px",
            }}
          >
            {isMapShown ? (
              <Map
                marker={
                  modalLocation
                    ? {
                        latitude: modalLocation.latitude,
                        longitude: modalLocation.longtitude,
                      }
                    : null
                }
              />
            ) : (
              renderPlaceholder()
            )}
          </div>
        </LocationWrapper>
      </Modal>
    </div>
  );
};

export default LocationPicker;
